import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { X, Mail, Plane, Wifi } from "lucide-react";

export function AuthModal({ open, onClose, defaultMode = "signin", redirectOnSuccess = true }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [role, setRole] = useState("traveler");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "signin") {
        u = await login(form.email, form.password);
      } else {
        u = await register({ ...form, role });
      }
      toast.success(mode === "signin" ? "Welcome back!" : "Account created!");
      onClose();
      if (redirectOnSuccess) {
        navigate(u.role === "admin" ? "/admin" : u.role === "host" ? "/dashboard" : "/search");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/search";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="auth-modal">
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 rise">
        <button onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground" data-testid="auth-close"><X size={20} /></button>
        <div className="flex items-center gap-2 mb-6"><Logo size={30} className="text-foreground" /><span className="font-display text-lg font-bold">OPNWiFi</span></div>

        <h2 className="font-display text-2xl font-bold tracking-tight">{mode === "signin" ? "Welcome back" : "Join the network"}</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-6">{mode === "signin" ? "Sign in to connect or host Wi-Fi." : "Verified sharing for travelers and neighbors."}</p>

        {mode === "signup" && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button type="button" onClick={() => setRole("traveler")} data-testid="role-traveler"
              className={`flex flex-col items-start gap-1 p-3 rounded-xl border transition-all ${role === "traveler" ? "border-foreground bg-accent" : "border-border"}`}>
              <Plane size={16} /><span className="text-sm font-medium">Traveler / Neighbor</span><span className="text-xs text-muted-foreground">Find & connect</span>
            </button>
            <button type="button" onClick={() => setRole("host")} data-testid="role-host"
              className={`flex flex-col items-start gap-1 p-3 rounded-xl border transition-all ${role === "host" ? "border-foreground bg-accent" : "border-border"}`}>
              <Wifi size={16} /><span className="text-sm font-medium">Host</span><span className="text-xs text-muted-foreground">Share & earn</span>
            </button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <input required value={form.name} onChange={upd("name")} placeholder="Full name" data-testid="auth-name"
              className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring transition-colors" />
          )}
          <input required type="email" value={form.email} onChange={upd("email")} placeholder="Email address" data-testid="auth-email"
            className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring transition-colors" />
          <input required type="password" value={form.password} onChange={upd("password")} placeholder="Password" data-testid="auth-password"
            className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring transition-colors" />
          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="w-full h-11 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="h-px flex-1 bg-border" /></div>

        <button onClick={googleAuth} data-testid="google-auth-btn"
          className="w-full h-11 rounded-xl border border-border flex items-center justify-center gap-2 text-sm hover:bg-accent transition-colors">
          <Mail size={16} /> Continue with Google
        </button>

        <p className="text-center text-sm text-muted-foreground mt-5">
          {mode === "signin" ? "New to OPNWiFi?" : "Already have an account?"}{" "}
          <button onClick={() => setMode(mode === "signin" ? "signup" : "signin")} data-testid="auth-switch-mode" className="text-foreground font-medium underline-offset-4 hover:underline">
            {mode === "signin" ? "Create account" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
